"use client"
import { createContext, useContext, type ReactNode } from "react"
import type { BranchKey, ComparisonPlanId, MetricId } from "../metricDetailsTypes"
import { METRIC_DETAILS, SUPPLEMENT_SLUGS, SUPPLEMENT_TITLES } from "../metricDetailsData"
import { metricNumericValueAtYear, metricValueAtYear } from "../MetricScenarioChart"
import { SLOWDOWN_TOTAL_TITLE } from "../MetricBarChart"
import { formatSlowdownYears } from "../../../indicators/slowdownTimeFormat"
import { getCapabilityThresholdLabelForValue } from "../../../extrapolated-graph/capabilityThresholds"
import { PowerRegionsCombo, PowerRegionsLiveBreakdown } from "../../blocks/power/powerHoverSwap"
import { POWER_METRIC_COPY } from "../../blocks/belowHudDetailSwap"
import {
  CapabilityLadderRows,
  type CapabilityLadderHighlight,
} from "../../blocks/capability/capabilityHoverSwap"
import { LaborDotStripLeaderRows } from "../../../labor-mix/dot-strip/LaborDotStripLeaderRows"
import {
  METRIC_PANEL_DEFINITIONS,
  OTHER_PLANS_GRAY,
  PanelBackButton,
  type PanelBackDesign,
  PanelChart,
  PanelClickedBlock,
  PanelDefinition,
  PanelLiveTicker,
  PanelReferenceProse,
  PanelSupplementLinks,
  PanelTitle,
  PanelYearReadout,
  useMetricPanelDetail,
} from "./panelParts"

// Five alternative treatments of the metric details panel, one per hidden HUD
// tab (?hud=1..5 — see HUD_METRIC_PANEL_DESIGNS in variants.tsx). All five
// share the same skeleton, per the brief:
//   top    — the clicked HUD block re-rendered live, its definition beside it,
//            the current story year, and a prominent back affordance;
//   middle — the live data (scenario chart + any richer live breakdown);
//   bottom — the reference zone: static definition prose, visually distinct
//            from the live zone, with the supplement links at its top.
// The sketch-layout ids (title-row / one-row / year-first / corner-dock) are
// Joanna's four hand-drawn arrangements for the click-open panel; the earlier
// chrome studies (strip / dossier / rail / ticker / ledger) remain available
// here but are off the tabs.
// The power-duo-back-* ids (tabs 11–15, chip icon studies on 17–21) are the
// power-duo layout with one alternative back-arrow treatment each — see
// PanelBackDesign / PanelBackChipIcon in panelParts.tsx.
export type MetricPanelDesignId =
  | "title-row"
  | "title-row-year-right"
  | "title-row-details-flush"
  | "power-duo"
  | "power-duo-back-red"
  | "power-duo-back-chip"
  | "power-duo-back-labeled"
  | "power-duo-back-long"
  | "power-duo-back-solid"
  | "power-duo-back-chip-arrow"
  | "power-duo-back-chip-chevron"
  | "power-duo-back-chip-chevrons"
  | "power-duo-back-chip-return"
  | "power-duo-back-chip-triangle"
  | "power-stack-2sent"
  | "power-stack-3col"
  | "power-stack-chart-side"
  | "power-stack-footnote"
  | "power-stack-menu-first"
  | "power-stack-chart-row"
  | "power-stack-explainer-3col"
  | "power-stack-explainer-2col"
  | "power-stack-chart-footnotes"
  | "power-stack-footnotes-3col"
  | "power-stack-intro-top"
  | "power-stack-hover-type"
  | "power-stack-section-titles"
  | "power-stack-temporal-titles"
  | "power-stack-framed"
  | "power-stack-framed-chart-sub"
  | "power-stack-hover-type-titles"
  | "power-stack-temporal-subtitled"
  | "power-stack-temporal-caption"
  | "power-stack-plans-3col"
  | "power-stack-data-behind"
  | "power-stack-header-links"
  | "power-stack-plans-grouped"
  | "power-stack-labor-strip"
  | "power-stack-aligned-plans"
  | "power-stack-gray-plans"
  | "power-stack-links-outline"
  | "power-stack-links-solid"
  | "power-stack-links-red-arrow"
  | "power-stack-links-bare"
  | "power-stack-links-pill"
  | "power-stack-links-outline-clean"
  | "minimal"
  | "one-row"
  | "year-first"
  | "corner-dock"
  | "strip"
  | "dossier"
  | "rail"
  | "ticker"
  | "ledger"

type PanelProps = {
  metricId: MetricId
  // Current HUD year (fractional), synced to scroll — drives the year readout,
  // the live values, and the chart's now-cursor.
  year: number
  onClose?: () => void
  // True on surfaces WITHOUT the overlay DetailsYearPill (the mobile
  // full-screen card): the header's year readout renders as real visible
  // text instead of the invisible landing slot the desktop overlay pill
  // glides onto — otherwise the year is simply missing there.
  staticYearPill?: boolean
}

// Shared outer frame: the article-facing hairline + left padding, matching
// the production details state so the column reads continuous with the story.
// `flush` (power-duo) drops both — no divider line, and everything (back
// arrow, content, Details) aligns with the HUD column's left edge so the
// panel spans the full HUD width.
const PanelFrame = ({ children, flush = false }: { children: ReactNode; flush?: boolean }) => (
  <div className={`relative text-[#111111] ${flush ? "" : "pl-6"}`}>
    {!flush && <div className="absolute left-0 inset-y-0 w-0 border-l border-gray-300" aria-hidden />}
    {children}
  </div>
)

// The details panel's capability-ladder treatment for the ACTIVE tab —
// provided per tab by Playbook.tsx from HUD_DETAILS_LADDER_HIGHLIGHTS
// (variants.tsx), so each tab's details page MATCHES its takeover ladder
// (Joanna 2026-07-12). The fallback (no provider, e.g. print surfaces) is
// the shared treatment most tabs use.
export const DetailsLadderHighlightContext =
  createContext<CapabilityLadderHighlight>("rail-glide-thick")

// Richer live data under the chart, where a metric has it: the full
// distribution-of-power breakdown, or the capability ladder with the current
// level (derived from the live chart value) highlighted.
const CapabilityLadderLive = ({ year }: { year: number }) => {
  const { detail, series } = useMetricPanelDetail("capability")
  const v = detail && series ? metricNumericValueAtYear(series, detail.axis, year) : null
  return (
    <CapabilityLadderRows
      currentLabel={v === null ? null : getCapabilityThresholdLabelForValue(v)}
      highlight={useContext(DetailsLadderHighlightContext)}
    />
  )
}

const PanelLiveExtras = ({ metricId, year }: PanelProps) => {
  if (metricId === "power") return <PowerRegionsLiveBreakdown />
  if (metricId === "capability") return <CapabilityLadderLive year={year} />
  return null
}

// Entrance animation classes matching the production content, so the panel
// sections settle in with the same rhythm the crossfade establishes.
const IN_ANIM = "animate-details-in [animation-delay:240ms] motion-reduce:animate-none"
const FADE_ANIM = "animate-details-fade [animation-delay:340ms] motion-reduce:animate-none"

// ── Design 1 · "strip" ───────────────────────────────────────────────────────
// One horizontal header strip: back square, the clicked block, a hairline,
// the definition, the year pill at the right edge. Live zone bare on the
// canvas under a pulsing LIVE readout; reference zone a tinted card with a
// heavy top rule.
const StripPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-start gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <PanelClickedBlock metricId={metricId} year={year} className="shrink-0" />
        <div className="w-0 self-stretch border-l border-black/20" aria-hidden />
        <div className="min-w-0 flex-1">
          <PanelTitle metricId={metricId} className="text-[12px]" />
          <PanelDefinition metricId={metricId} className="mt-1" />
        </div>
      </div>
      <PanelYearReadout year={year} />
    </div>

    <div className={IN_ANIM}>
      <PanelLiveTicker metricId={metricId} year={year} className="mt-5" />
      <div className="mt-2">
        <PanelChart metricId={metricId} year={year} />
      </div>
      <div className="mt-4">
        <PanelLiveExtras metricId={metricId} year={year} />
      </div>
    </div>

    <div className={`mt-6 rounded-md border-t-2 border-black bg-black/[0.04] p-4 ${IN_ANIM}`}>
      <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-black/60">
        Reference
      </div>
      <PanelSupplementLinks metricId={metricId} className="mt-1.5" />
      <PanelReferenceProse metricId={metricId} className="mt-3" />
    </div>
  </PanelFrame>
)

// ── Design 2 · "dossier" ─────────────────────────────────────────────────────
// Big-year masthead: back at the left, the year as large numerals at the
// right. Live zone marked by an accent left border; the reference zone
// inverts to dark so the two registers can't be confused.
const DossierPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-start justify-between gap-3 ${FADE_ANIM}`}>
      <div className="flex items-start gap-3">
        <PanelBackButton onClose={onClose} />
        <PanelTitle metricId={metricId} className="mt-3" />
      </div>
      <div
        data-metric-panel-year
        className="font-mono text-[30px] font-bold leading-none tabular-nums"
        aria-label={`Story year ${Math.floor(year)}`}
      >
        {Math.floor(year)}
      </div>
    </div>

    <div className={`mt-4 flex items-start gap-4 ${IN_ANIM}`}>
      <PanelClickedBlock metricId={metricId} year={year} className="shrink-0" />
      <PanelDefinition metricId={metricId} className="min-w-0 flex-1 text-[11px]" />
    </div>

    <div className={`mt-5 border-l-2 pl-3 ${IN_ANIM}`} style={{ borderColor: "var(--accent)" }}>
      <PanelLiveTicker metricId={metricId} year={year} />
      <div className="mt-2">
        <PanelChart metricId={metricId} year={year} />
      </div>
      <div className="mt-4">
        <PanelLiveExtras metricId={metricId} year={year} />
      </div>
    </div>

    <div className={`mt-6 rounded-md bg-[#161616] p-4 text-[#fffff8] ${IN_ANIM}`}>
      <div className="font-mono text-[10px] font-bold uppercase tracking-wider opacity-70">
        Definitions &amp; sources
      </div>
      <PanelSupplementLinks metricId={metricId} tone="dark" className="mt-1.5" />
      <PanelReferenceProse metricId={metricId} tone="dark" className="mt-3" />
    </div>
  </PanelFrame>
)

// ── Design 3 · "rail" ────────────────────────────────────────────────────────
// A full-height rail on the left carries the back arrow, a connecting line,
// and the year reading vertically — the panel's chrome lives in the rail, the
// content column stays clean. Reference sits under a double rule.
const RailPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className="grid grid-cols-[44px_1fr] gap-x-4">
      <div className={`flex flex-col items-center gap-2 ${FADE_ANIM}`}>
        <PanelBackButton onClose={onClose} />
        <div className="w-0 flex-1 border-l border-black/25" aria-hidden />
        <div
          data-metric-panel-year
          className="font-mono text-[15px] font-bold tabular-nums tracking-[0.1em]"
          style={{ writingMode: "vertical-rl" }}
          aria-label={`Story year ${Math.floor(year)}`}
        >
          {Math.floor(year)}
        </div>
      </div>

      <div>
        <div className={`flex items-start gap-4 ${FADE_ANIM}`}>
          <PanelClickedBlock metricId={metricId} year={year} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <PanelTitle metricId={metricId} className="text-[13px]" />
            <PanelDefinition metricId={metricId} className="mt-1.5" />
          </div>
        </div>

        <div className={IN_ANIM}>
          <PanelLiveTicker metricId={metricId} year={year} className="mt-5" />
          <div className="mt-2">
            <PanelChart metricId={metricId} year={year} />
          </div>
          <div className="mt-4">
            <PanelLiveExtras metricId={metricId} year={year} />
          </div>
        </div>

        <div className={`mt-6 pt-3 [border-top:3px_double_#111] ${IN_ANIM}`}>
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-black/60">
            Definitions
          </div>
          <PanelSupplementLinks metricId={metricId} className="mt-1.5" />
          <PanelReferenceProse metricId={metricId} className="mt-3" />
        </div>
      </div>
    </div>
  </PanelFrame>
)

// ── Design 4 · "ticker" ──────────────────────────────────────────────────────
// The live zone is an instrument: everything updating sits inside one
// hairline-boxed band headed "● LIVE · <year>". The reference below reads as
// documentation — serif, roomier leading — so the registers feel unmistakably
// different.
const TickerPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <PanelTitle metricId={metricId} className="min-w-0 flex-1" />
      <PanelYearReadout year={year} />
    </div>

    <div className={`mt-4 rounded-[6px] border border-black ${IN_ANIM}`}>
      <div className="flex items-baseline justify-between gap-2 border-b border-black/20 px-3 py-2">
        <PanelLiveTicker metricId={metricId} year={year} />
        <span className="font-mono text-[10px] tabular-nums text-black/60">
          {Math.floor(year)}
        </span>
      </div>
      <div className="px-3 py-3">
        <div className="flex items-start gap-4">
          <PanelClickedBlock metricId={metricId} year={year} className="shrink-0" />
          <PanelDefinition metricId={metricId} className="min-w-0 flex-1" />
        </div>
        <div className="mt-3">
          <PanelChart metricId={metricId} year={year} />
        </div>
        <div className="mt-4">
          <PanelLiveExtras metricId={metricId} year={year} />
        </div>
      </div>
    </div>

    <div
      className={`mt-6 ${IN_ANIM}`}
      style={{ fontFamily: "et-book, Palatino, 'Palatino Linotype', serif" }}
    >
      <div className="text-[15px] italic text-black/70">About this metric</div>
      <PanelSupplementLinks metricId={metricId} className="mt-1.5" />
      <PanelReferenceProse
        metricId={metricId}
        className="mt-3 [&_.font-system-ui]:!font-[inherit] [&_span]:text-[14.5px] [&_div]:text-[14.5px]"
      />
    </div>
  </PanelFrame>
)

// ── Design 5 · "ledger" ──────────────────────────────────────────────────────
// The clicked block docks in a bordered card at the top; the reference zone
// is a folder — a black tab reading FIELD GUIDE over a bordered card whose
// first row is the supplement links as chips.
const LedgerPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-center justify-between gap-3 ${FADE_ANIM}`}>
      <div className="flex items-center gap-3">
        <PanelBackButton onClose={onClose} />
        <PanelTitle metricId={metricId} />
      </div>
      <PanelYearReadout year={year} />
    </div>

    <div className={`mt-4 flex items-start gap-4 rounded-[6px] border border-black/40 p-3 ${IN_ANIM}`}>
      <PanelClickedBlock metricId={metricId} year={year} className="shrink-0" />
      <PanelDefinition metricId={metricId} className="min-w-0 flex-1" />
    </div>

    <div className={IN_ANIM}>
      <PanelLiveTicker metricId={metricId} year={year} className="mt-5" />
      <div className="mt-2">
        <PanelChart metricId={metricId} year={year} />
      </div>
      <div className="mt-4">
        <PanelLiveExtras metricId={metricId} year={year} />
      </div>
    </div>

    <div className={`mt-7 ${IN_ANIM}`}>
      <div className="inline-block rounded-t-[6px] border border-b-0 border-black bg-black px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#fffff8]">
        Field guide
      </div>
      <div className="rounded-b-[6px] rounded-tr-[6px] border border-black p-4">
        <PanelSupplementLinks metricId={metricId} asChips />
        <PanelReferenceProse metricId={metricId} className="mt-3" />
      </div>
    </div>
  </PanelFrame>
)

// ─── Sketch layouts ──────────────────────────────────────────────────────────
// Shared lower half of all four sketch layouts: the live zone (pulsing
// readout, the metric's richer live data, the scenario chart) and the Details
// section — definition-register reference prose under a mono DETAILS header,
// supplement links first.
const SketchLiveZone = ({
  metricId,
  year,
  // Tab 10 drops the pulsing "LIVE — value" line; the breakdown and chart
  // carry the live register on their own.
  hideTicker = false,
}: PanelProps & { hideTicker?: boolean }) => (
  <div className={IN_ANIM}>
    {!hideTicker && <PanelLiveTicker metricId={metricId} year={year} className="mt-4" />}
    <div className={hideTicker ? "mt-4" : "mt-2.5"}>
      <PanelLiveExtras metricId={metricId} year={year} />
    </div>
    <div className="mt-3">
      <PanelChart metricId={metricId} year={year} />
    </div>
  </div>
)

const SketchDetailsSection = ({ metricId }: { metricId: MetricId }) => (
  <div className={`mt-7 border-t border-black/50 pt-2.5 ${IN_ANIM}`}>
    <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-black/60">
      Details
    </div>
    <PanelSupplementLinks metricId={metricId} className="mt-1.5" />
    <div className="mt-3">
      <PanelDefinition metricId={metricId} className="mb-3 text-[11px] opacity-80" />
      <PanelReferenceProse metricId={metricId} />
    </div>
  </div>
)

// Sketch 1 · "title-row": back arrow + title on the first line, the year pill
// alone on the second, then the clicked block spanning the column.
const TitleRowPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <PanelTitle metricId={metricId} />
    </div>
    <div className={`mt-3 ${FADE_ANIM}`}>
      <PanelYearReadout year={year} />
    </div>
    <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-3" />
    <SketchLiveZone metricId={metricId} year={year} onClose={onClose} />
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// "title-row-year-right": the title-row layout, but the year pill rides the
// SAME row as the back arrow and title, flying over to the right edge instead
// of settling on its own line.
const TitleRowYearRightPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <PanelTitle metricId={metricId} className="min-w-0 flex-1" />
      <PanelYearReadout year={year} />
    </div>
    <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-4" />
    <SketchLiveZone metricId={metricId} year={year} onClose={onClose} hideTicker />
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// "title-row-details-flush" (tab 12): the title-row-year-right layout with
// the title reading "<METRIC> DETAILS", the article divider line removed,
// and the content expanded to the full HUD width (flush frame).
const TitleRowDetailsFlushPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame flush>
    <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <PanelTitle metricId={metricId} suffix=" details" className="min-w-0 flex-1" />
      <PanelYearReadout year={year} />
    </div>
    <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-4" />
    <SketchLiveZone metricId={metricId} year={year} onClose={onClose} hideTicker />
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// "power-duo" (tab 11): under the title-row-year-right header, the panel
// leads with a 1:1 reproduction of the HOVER view — the US/China/Rest-of-World
// combo columns on the left, the treemap at its on-HUD size on the right — so
// on click the hover columns glide straight up ([data-hud-power-breakdown] →
// [data-metric-panel-power-breakdown]) and the treemap flies at constant
// size. The full-width scenario chart follows, then Details. Non-power
// metrics fall back to the title-row-year-right arrangement.
const PowerDuoPanel = ({
  metricId,
  year,
  onClose,
  backDesign,
}: PanelProps & { backDesign?: PanelBackDesign }) => (
  <PanelFrame flush>
    <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} design={backDesign} />
      <PanelTitle metricId={metricId} className="min-w-0 flex-1" />
      <PanelYearReadout year={year} />
    </div>
    {metricId === "power" ? (
      <>
        {/* NO entrance animation: this row's geometry matches the hover state
            exactly — combo at the HUD's left column x/width, treemap at its
            on-HUD box — so the travellers land on completely still targets
            and the swap is invisible. */}
        <div className="mt-4 flex items-start gap-[20px]">
          <div data-metric-panel-power-breakdown className="min-w-0 flex-1">
            <PowerRegionsCombo />
          </div>
          <PanelClickedBlock metricId={metricId} year={year} size="hud" className="w-[220px] shrink-0" />
        </div>
        <div className={`mt-5 ${IN_ANIM}`}>
          <PanelChart metricId={metricId} year={year} />
        </div>
      </>
    ) : (
      <>
        <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-4" />
        <SketchLiveZone metricId={metricId} year={year} onClose={onClose} hideTicker />
      </>
    )}
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// ─── Power-stack panels (tabs 22–26) ─────────────────────────────────────────
// The hover→details TRANSITION study for Distribution of Power. The panel
// keeps as much of the hover state in place as possible: the treemap expands
// to the FULL HUD width and stays at the top (a pure horizontal stretch — it
// keeps its on-HUD 120px height), the hover menu's three region columns stay
// BELOW (the below-HUD breakdown flies into a slot in the same lower region),
// and the scenario chart slots between them. The reference prose is condensed
// to two sentences per section (POWER_STACK_COPY below); the five tabs vary
// how — and whether — it renders. The whole panel is budgeted to fit above a
// ~725px fold. Non-power metrics fall back to the power-duo arrangement.

// Condensed 2-sentence reference copy, distilled from METRIC_DETAILS.power's
// full prose. Edit the sentences right here.
export const POWER_STACK_COPY = {
  whatHeading: "What this is",
  what: "Power increasingly comes from controlling the best AIs, so we proxy its concentration by the largest single holder’s share of world frontier compute. A falling share means power is spreading out.",
  DHeading: "Default path",
  D: "Algorithmic secrets remain the companies’ main moat, and money flows to whoever controls the best AIs, so pooling continues past 2029. The largest holder’s share rises to ~30% by 2031 and ~50% by 2035 — a handful of firms in one country controlling the frontier.",
  AHeading: "Plan A",
  A: "Total Research Transparency publishes the core algorithms, evaporating the leaders’ moat and giving other companies and countries time to catch up. The largest holder’s share stops climbing and holds near ~20% through 2040 while the number of frontier companies keeps growing.",
}

// Same line-swatch vocabulary as the chart's post-fork hierarchy
// (MetricScenarioChart, 2026-07-09): Plan A = heavy black long dash,
// Default = red mid-weight dash. `width` shrinks the segment (the
// plans-3col columns use 12px so "Plan D (default)" never wraps).
const SwBranchStack = ({ branch, width = 18 }: { branch: BranchKey; width?: number }) =>
  branch === "A" ? (
    <svg width={width} height="8" viewBox={`0 0 ${width} 8`} className="inline-block align-middle" aria-hidden>
      <line x1="0" y1="4" x2={width} y2="4" stroke="#111111" strokeWidth="2" strokeDasharray="5 2.5" />
    </svg>
  ) : (
    <svg width={width} height="8" viewBox={`0 0 ${width} 8`} className="inline-block align-middle" aria-hidden>
      <line x1="0" y1="4" x2={width} y2="4" stroke="var(--race-background)" strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  )

// Shortened swatch for the plans-3col columns (see SwBranchStack).
export const POWER_STACK_COMPACT_SWATCH_WIDTH_PX = 12

// One scenario section: swatch + colored mono header, then the 2-sentence body.
const PowerStackScenarioBlock = ({
  branch,
  heading,
  body,
  bodyClassName = "text-[13px]",
  headingClassName = "text-[10px]",
  swatchWidth,
  className,
}: {
  branch: BranchKey
  heading: string
  body: ReactNode
  bodyClassName?: string
  headingClassName?: string
  swatchWidth?: number
  className?: string
}) => (
  <div className={className}>
    <div className="flex items-center gap-2">
      <SwBranchStack branch={branch} width={swatchWidth} />
      <b
        className={`font-mono font-bold uppercase tracking-wider ${headingClassName}`}
        style={{ color: branch === "D" ? "var(--race-background)" : "#111111" }}
      >
        {heading}
      </b>
    </div>
    {/* Reference prose reads in the essay's own serif (2026-07-09: the
        system-sans register read as a different product next to the
        article) — mono stays the voice of labels, values, and titles. */}
    <div className={`mt-1 font-et-book leading-[1.45] ${bodyClassName}`}>{body}</div>
  </div>
)

// Heading over the stacked supplement links in the explainer-3col reference.
export const POWER_STACK_READ_MORE_HEADING = "Read more"

// Section titles gluing the panel's two zones together (tabs 34–36): the
// treemap + region columns are a live SNAPSHOT at the story year; the chart
// is the largest holder's share OVER TIME — the snapshot is one point on it.
// "content" names what each zone shows; "temporal" frames them as
// now-vs-trajectory (the now title takes the live story year).
export const POWER_STACK_SECTION_TITLES = {
  content: {
    now: "Who holds frontier compute",
    trajectory: "The largest holder’s share, 2024 → 2040",
  },
  temporal: {
    now: (year: number) => `Now — ${year}`,
    trajectory: "Largest single share of frontier compute",
  },
}

// The zone titles' shared look: the hover takeover's header voice.
const PowerStackSectionTitle = ({
  children,
  sizeClass = "text-[10px]",
  className,
}: {
  children: ReactNode
  sizeClass?: string
  className?: string
}) => (
  <div className={`font-mono font-bold uppercase tracking-wider ${sizeClass} ${className ?? ""}`}>
    {children}
  </div>
)

// One-line subtitles under the zone titles (tab 39). The NOW zone keeps the
// hover state's own subtitle verbatim (same const the hover renders —
// POWER_METRIC_COPY.definition — so the two can never drift); the trajectory
// zone gets its own one-liner. Rendered at the panel's body size.
export const POWER_STACK_SECTION_SUBTITLES = {
  now: POWER_METRIC_COPY.definition,
  trajectory:
    "The largest single holder’s share of that compute under each plan — a falling line means power is spreading out.",
}

const PowerStackSectionSubtitle = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={`font-system-ui text-[13px] leading-normal ${className ?? ""}`}>{children}</div>
)

// Figure caption under the treemap (tab 40) — the hover's short "measured"
// line, same const the hover renders so the two never drift.
export const POWER_STACK_TREEMAP_CAPTION = POWER_METRIC_COPY.measuredInline

// Tab 41 — the plans-3col reference: the first column names the default line
// as Plan D, and the third lists every OTHER plan with a one-line power
// trajectory (values read off planBranchSeries.power; plan monikers from
// lib/branchTypes' planLabels).
// Tab 42's panel title — replaces the metric label in the header.
export const POWER_STACK_DATA_TITLE = "Data behind distribution of power"

export const POWER_STACK_PLAN_D_HEADING = "Plan D (default path)"
// Tab 44's shorter variant of the same heading.
export const POWER_STACK_PLAN_D_SHORT_HEADING = "Plan D (default)"

// Tab 44: px shaved off the chart's bottom (see chartBottomCropPx).
export const POWER_STACK_CHART_BOTTOM_CROP_PX = 13
export const POWER_STACK_OTHER_PLANS_HEADING = "Other plans"
export const POWER_STACK_OTHER_PLANS: Array<{ id: ComparisonPlanId; name: string; trajectory: string }> = [
  { id: "B", name: "Plan B (sabotage)", trajectory: "Concentrates hardest — the top holder nears 80% by 2034." },
  { id: "C", name: "Plan C (the slowdown)", trajectory: "Pooling continues to ~50% by 2032." },
  { id: "S", name: "Plan S", trajectory: "Spreads power out — the top share falls to ~7% by 2040." },
]

// Tab 46: the Other-plans one-liners for the ALIGNED metric panels — same
// voice as POWER_STACK_OTHER_PLANS. Thomas's minimal rewrite (2026-07-09);
// most metrics fold B and C into one "Plans B & C" line, alignment keeps
// them separate. Slowdown keeps its series-derived numbers (no rewrite yet).
export const METRIC_OTHER_PLANS: Partial<Record<MetricId, Array<{ id: ComparisonPlanId; name: string; trajectory: string }>>> = {
  labor: [
    { id: "B", name: "Plans B & C", trajectory: "Similar to Plan D, except slightly delayed." },
    { id: "S", name: "Plan S", trajectory: "Machine labor is fixed at roughly 2029 levels, before full automation." },
  ],
  income: [
    { id: "B", name: "Plans B & C", trajectory: "Similar to Plan D, but with a slight delay." },
    { id: "S", name: "Plan S", trajectory: "Limited AI deployment, so status quo growth continues." },
  ],
  employment: [
    { id: "B", name: "Plans B & C", trajectory: "Similar to Plan D, but with a slight delay." },
    { id: "S", name: "Plan S", trajectory: "Prevents AI automation, and so employment remains similar to the status quo." },
  ],
  safetyTax: [
    { id: "B", name: "Plan B (sabotage)", trajectory: "Rises to 2 years by 2032 and holds there." },
    { id: "C", name: "Plan C (the slowdown)", trajectory: "Costs about 4 months by 2031." },
    { id: "S", name: "Plan S", trajectory: "Compounds to ~11 years by 2040." },
  ],
  alignment: [
    { id: "B", name: "Plan B (sabotage)", trajectory: "US megaproject securitizes and limits access, reducing the number of alignment researchers." },
    { id: "C", name: "Plan C (the slowdown)", trajectory: "Leading AI companies securitize, and winner-take-all dynamics reduce the number of alignment researchers at the frontier." },
    { id: "S", name: "Plan S", trajectory: "The frontier is fixed at a low level, so an increasing number of researchers are able to work on frontier models." },
  ],
  capability: [
    { id: "B", name: "Plans B & C", trajectory: "Similar to Plan D, except slightly delayed." },
    { id: "S", name: "Plan S", trajectory: "A moratorium on further capability scaling freezes uplift." },
  ],
}

// The supplement links stacked as their own column (the explainer-3col
// reference's third column), instead of the inline "Explained more in …"
// sentence.
const PowerStackReadMoreColumn = ({
  className,
  headingClassName = "text-[10px]",
  linkClassName = "text-[12px]",
}: {
  className?: string
  headingClassName?: string
  linkClassName?: string
}) => {
  const slugs = METRIC_DETAILS.power?.sources.filter((s) => SUPPLEMENT_SLUGS.has(s)) ?? []
  if (slugs.length === 0) return null
  return (
    <div className={className}>
      <b className={`font-mono font-bold uppercase tracking-wider ${headingClassName}`}>{POWER_STACK_READ_MORE_HEADING}</b>
      <ul className="mt-1 grid list-none gap-1 p-0">
        {slugs.map((slug) => (
          <li key={slug}>
            <a
              href={`/supplements/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`font-system-ui leading-normal text-[#2A623D] underline decoration-[#2A623D]/30 underline-offset-2 hover:decoration-[#2A623D] ${linkClassName}`}
            >
              {SUPPLEMENT_TITLES[slug] ?? slug}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

// One scenario as a RUN-IN figure footnote riding directly under the chart:
// swatch, colored mono label, an em-dash, then the two sentences inline.
const PowerStackChartFootnote = ({
  branch,
  heading,
  body,
  className,
}: {
  branch: BranchKey
  heading: string
  body: string
  className?: string
}) => (
  <div className={`font-system-ui text-[11px] leading-normal ${className ?? ""}`}>
    <span className="mr-2 inline-block align-middle">
      <SwBranchStack branch={branch} />
    </span>
    <b
      className="font-mono text-[10px] font-bold uppercase tracking-wider"
      style={{ color: branch === "D" ? "var(--race-background)" : "#111111" }}
    >
      {heading}
    </b>
    {" — "}
    {body}
  </div>
)

// How the condensed reference renders (one per tab):
//   "sections"        — the two scenario blocks stacked full-width (tabs 22, 26)
//   "columns"         — What-this-is / Default / Plan A as three skimmable
//                       columns
//   "chart-side"      — the scenario blocks ride BESIDE the chart, killing the
//                       reference band entirely
//   "footnote"        — no scenario prose at all: one mono definition line +
//                       links
//   "explainer-3col"  — the what-this-is explainer full-width under the chart,
//                       then Default / Plan A / Read-more as three columns
//   "explainer-2col"  — the same explainer, Default / Plan A as TWO columns,
//                       the links as a closing footnote line
//   "chart-footnotes" — Default / Plan A as run-in figure footnotes hugging
//                       the chart (they explain its two line styles), the
//                       explainer + links as the bottom footnote
//   "footnotes-3col"  — the same figure footnotes, but as THREE columns
//                       hugging the chart: one per line style plus a
//                       Read-more column; the explainer alone at the bottom
//   "plans-3col"      — Plan D (default path) / Plan A / an Other-plans list
//                       (every remaining plan with a one-line trajectory) —
//                       no Read-more column
type PowerStackReference =
  | "sections"
  | "columns"
  | "chart-side"
  | "footnote"
  | "explainer-3col"
  | "explainer-2col"
  | "chart-footnotes"
  | "footnotes-3col"
  | "plans-3col"

// Tab 27 ("chart-row"): instead of the full-width treemap band, the header is
// followed by ONE row — the scenario chart on the LEFT half, the treemap on
// the RIGHT at its exact on-HUD geometry (220×120), so on click the treemap
// barely moves at all. The chart draws in a compressed viewBox
// (POWER_STACK_CHART_ROW_CHART_WIDTH_PX) so its labels keep their authored
// size instead of scaling down. 455 (HUD width) − 220 (treemap) − 20 (gap).
const POWER_STACK_CHART_ROW_CHART_WIDTH_PX = 215

// Trajectory zone title for the aligned non-power panels — same voice as
// POWER_STACK_SECTION_TITLES.temporal.trajectory, spanning the metric's own
// chart years.
export const POWER_STACK_TRAJECTORY_TITLE = (firstYear: number, lastYear: number) =>
  `Trajectory — ${firstYear} → ${lastYear}`

// Tab 45: the aligned LABOR panel's short title — replaces the metric label
// ("Machine Labor") in the header when the labor-strip treatment is on.
export const ALIGNED_LABOR_TITLE = "Labor"

// Tab 46: the stat-row metrics whose NOW zone is just the live number — the
// header already names the metric, so the HUD block's label chip is noise.
const ALIGNED_BARE_STAT_METRICS = new Set<MetricId>(["employment", "income", "alignment", "safetyTax"])

// Tab 46: bare-number sizes. The stat metrics' single number leads the page
// at the big-stat 24px (raised from PanelTitle's 17px on 2026-07-07 —
// Joanna wanted the single number bigger); capability keeps the same 24px
// (only the threshold tag under it is dropped — the ladder below repeats it).
export const ALIGNED_BARE_STAT_SIZE_CLASS = "text-[24px]"
export const ALIGNED_CAPABILITY_STAT_SIZE_CLASS = "text-[24px]"

// Capability's NOW zone (capabilityNoLevel): NO big number — the definition
// sits left (wrapping as needed) and the live speedup rides the right edge
// in the HOVER menu's exact stat treatment (StatValueLine in
// capabilityHoverSwap: mono label + bold value sharing one baseline).
// Sizes/label are literal copies of CAPABILITY_HOVER_STYLE /
// CAPABILITY_HOVER_COPY — keep in sync by hand if the hover side changes.
export const ALIGNED_CAPABILITY_SPEEDUP_STYLE = {
  label: "R&D Speedup",
  labelFontSize: "10px",
  valueFontSize: "12px",
}

// The right-aligned speedup line, in PanelClickedBlock's flight slot
// (data-metric-panel-block) so open/close choreography is unchanged.
const AlignedCapabilitySpeedupLine = ({ year }: { year: number }) => {
  const { detail, series } = useMetricPanelDetail("capability")
  if (!detail || !series) return null
  return (
    <div
      data-metric-panel-block
      className="flex shrink-0 items-baseline gap-3 whitespace-nowrap font-mono"
    >
      <span style={{ fontSize: ALIGNED_CAPABILITY_SPEEDUP_STYLE.labelFontSize }}>
        {ALIGNED_CAPABILITY_SPEEDUP_STYLE.label}
      </span>
      <span
        className="font-bold"
        style={{ fontSize: ALIGNED_CAPABILITY_SPEEDUP_STYLE.valueFontSize }}
      >
        {metricValueAtYear(series, detail.axis, year)}
      </span>
    </div>
  )
}

// The bare live number, in PanelClickedBlock's flight slot
// (data-metric-panel-block) so open/close choreography is unchanged. The
// slowdown metric alone appends its time unit — its chart series is a bare
// year count ("0.1"), so it renders through the shared slowdown vocabulary
// (formatSlowdownYears in indicators/slowdownTimeFormat.ts, the same
// formatter the HUD's Total Slowdown figure uses); the other stat metrics
// carry their unit in the value itself ("62%", "$45K").
const AlignedStatNumber = ({
  metricId,
  year,
  sizeClass = ALIGNED_BARE_STAT_SIZE_CLASS,
  className,
}: {
  metricId: MetricId
  year: number
  sizeClass?: string
  className?: string
}) => {
  const { detail, series } = useMetricPanelDetail(metricId)
  if (!detail || !series) return null
  const numericYears =
    metricId === "safetyTax" ? metricNumericValueAtYear(series, detail.axis, year) : null
  return (
    <div
      data-metric-panel-block
      className={`font-mono font-bold leading-none tabular-nums ${sizeClass} ${className ?? ""}`}
    >
      {numericYears !== null
        ? formatSlowdownYears(numericYears)
        : metricValueAtYear(series, detail.axis, year)}
    </div>
  )
}

// ─── Header supplement-link treatments (tabs 48–52) ──────────────────────────
// Joanna's mock: the header's supplement links as outlined chips with a
// leading ↗ — more prominent than the plain green text links. Five
// treatments, translated to the HUD's black/red ink, each varying ONE axis:
//   "text"      — production: green underlined text links, stacked (tabs ≤47)
//   "outline"   — tab 48: the mock in HUD ink — chip-padded leading-↗ label,
//                 reds on hover; its 1px hairline border was removed
//                 2026-07-07 at Joanna's request (vs "bare": keeps the
//                 chip's padding box and hover target). The label keeps the
//                 site's link underline, thickness pinned across hover.
//   "outline-clean" — tab 53: what she originally meant — the bordered
//                 hairline chip with the text underline removed
//   "solid"     — tab 49: inversion — solid black chip, cream label + ↗ (the
//                 back button's exact vocabulary), red fill on hover
//   "red-arrow" — tab 50: color accent — the outline chip but the ↗ is
//                 permanently red (red-as-icon), label/border stay black
//   "bare"      — tab 51: chrome-less — no box, just the bold mono label with
//                 the leading ↗, red on hover; prominence from weight alone
//   "pill"      — tab 52: geometry — fully-rounded outline pill with the ↗
//                 TRAILING the label (exit-arrow reading)
export type PanelHeaderLinkDesign = "text" | "outline" | "outline-clean" | "solid" | "red-arrow" | "bare" | "pill"

export const PANEL_HEADER_LINK_STYLE = {
  labelFontSize: "9.5px",
  chipGapPx: 4,
  iconGapPx: 5,
  paddingX: 7,
  paddingY: 5,
  radiusPx: 3,
  pillRadiusPx: 999,
  iconSizePx: 9,
  iconStrokeWidth: 1.5,
}

// The ↗ glyph as a true vector (like the back-chip icons): text arrows ride
// their font baseline and won't center in a chip.
const HeaderLinkArrow = ({ color }: { color?: string }) => (
  <svg
    width={PANEL_HEADER_LINK_STYLE.iconSizePx}
    height={PANEL_HEADER_LINK_STYLE.iconSizePx}
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M1.5 8.5 L8.5 1.5 M3 1.5 H8.5 V7"
      stroke={color ?? "currentColor"}
      strokeWidth={PANEL_HEADER_LINK_STYLE.iconStrokeWidth}
    />
  </svg>
)

// The header's supplement links, stacked right-aligned (the year pill's old
// slot). Every chip design keeps a constant border box — only colors change
// on hover, so the header never shifts.
export const PanelHeaderSupplementLinks = ({
  slugs,
  design,
}: {
  slugs: string[]
  design: PanelHeaderLinkDesign
}) => {
  if (slugs.length === 0) return null
  const S = PANEL_HEADER_LINK_STYLE
  return (
    <div className="flex shrink-0 flex-col items-end" style={{ gap: S.chipGapPx }}>
      {slugs.map((slug) => {
        const label = SUPPLEMENT_TITLES[slug] ?? slug
        if (design === "text") {
          return (
            <a
              key={slug}
              href={`/supplements/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-system-ui text-[11px] leading-normal text-[#2A623D] underline decoration-[#2A623D]/30 underline-offset-2 hover:decoration-[#2A623D]"
            >
              {label}
            </a>
          )
        }
        const chipColors = {
          // 2026-07-07: Joanna asked to drop this design's hairline border —
          // it keeps the chip's padding box (hover target, stacked rhythm)
          // but draws no outline. The label keeps tufte.css's link underline,
          // pinned to its resting 1.5px so hover only recolors (the global
          // a:hover rule switches thickness to from-font — overridden here).
          outline: "text-[#111111] decoration-[1.5px] hover:decoration-[1.5px] hover:text-[var(--accent)]",
          // Tab 53 — the version she originally meant: the hairline chip
          // WITH its border, the text underline removed.
          "outline-clean": "border border-[#111111] text-[#111111] no-underline hover:border-[var(--accent)] hover:text-[var(--accent)]",
          solid: "bg-[#111111] text-[#fffff8] hover:bg-[var(--accent)]",
          "red-arrow": "border border-[#111111] text-[#111111] hover:border-[var(--accent)] hover:text-[var(--accent)]",
          bare: "text-[#111111] hover:text-[var(--accent)]",
          pill: "border border-[#111111] text-[#111111] hover:border-[var(--accent)] hover:text-[var(--accent)]",
        }[design]
        const boxed = design !== "bare"
        const arrow = (
          <HeaderLinkArrow color={design === "red-arrow" ? "var(--accent)" : undefined} />
        )
        return (
          <a
            key={slug}
            href={`/supplements/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center font-mono font-bold leading-none transition-colors ${chipColors}`}
            style={{
              fontSize: S.labelFontSize,
              gap: S.iconGapPx,
              ...(boxed && {
                padding: `${S.paddingY}px ${S.paddingX}px`,
                borderRadius: design === "pill" ? S.pillRadiusPx : S.radiusPx,
              }),
            }}
          >
            {design === "pill" ? (
              <>
                {label}
                {arrow}
              </>
            ) : (
              <>
                {arrow}
                {label}
              </>
            )}
          </a>
        )
      })}
    </div>
  )
}

// Tab 46: the aligned panels' third reference column — pixel-identical markup
// to the power panel's Other-plans column (plans-3col reference), fed
// per-metric from METRIC_OTHER_PLANS. Heading in OTHER_PLANS_GRAY, matching
// the B / C / S chart lines (baseline since 2026-07-07).
const AlignedOtherPlansColumn = ({ metricId }: { metricId: MetricId }) => {
  const plans = METRIC_OTHER_PLANS[metricId]
  if (!plans) return null
  return (
    <div className="min-w-0 flex-1">
      <b
        className="font-mono text-[11px] font-bold uppercase tracking-wider"
        style={{ color: OTHER_PLANS_GRAY }}
      >
        {POWER_STACK_OTHER_PLANS_HEADING}
      </b>
      <ul className="mt-1 grid list-none gap-1.5 p-0">
        {plans.map((p) => (
          <li key={p.id} className="font-et-book text-[13px] leading-[1.45]">
            <b className="font-bold">{p.name}</b> — {p.trajectory}
          </li>
        ))}
      </ul>
    </div>
  )
}

// Tab 44's layout translated to the OTHER metrics, so every details panel
// speaks the power panel's design: header with the metric's supplement links
// stacked right (no year pill), a NOW zone (live clicked block + the hover's
// one-line definition as its caption; capability adds its ladder — the
// breakdown-equivalent), a TRAJECTORY zone (title over the metric's own year
// span, the full explanation at the hover body size, the bottom-cropped
// chart), and the Plan D (default) / Plan A scenario columns. No Other-plans
// column — that copy exists only for power.
// laborStrip (tab 45): the labor panel titles itself ALIGNED_LABOR_TITLE and
// its NOW visual is the HUD's own dot strip + inline legend (the hover
// state's visual) instead of the big effective-labor number.
// Tab 46 knobs: otherPlans adds the power panel's third reference column
// (METRIC_OTHER_PLANS); bareStats renders the stat-row metrics' NOW zone as
// just the title-sized number; capabilityNoLevel drops the capability big
// stat's threshold tag (the ladder below already names the level).
// Tab 47 knob: definitionUnderTitle moves the one-line definition up under
// the NOW zone title (above the number/visual) as its subtitle. (Tab 47's
// other treatment — non-Plan-D lines/heading in OTHER_PLANS_GRAY — became
// the baseline for all panels 2026-07-07 and no longer needs a knob.)
const PowerStackAlignedMetricPanel = ({
  metricId,
  year,
  onClose,
  laborStrip = false,
  otherPlans = false,
  bareStats = false,
  capabilityNoLevel = false,
  definitionUnderTitle = false,
  headerLinkDesign = "text",
}: PanelProps & {
  laborStrip?: boolean
  otherPlans?: boolean
  bareStats?: boolean
  capabilityNoLevel?: boolean
  definitionUnderTitle?: boolean
  headerLinkDesign?: PanelHeaderLinkDesign
}) => {
  const { detail } = useMetricPanelDetail(metricId)
  const slugs = METRIC_DETAILS[metricId]?.sources.filter((s) => SUPPLEMENT_SLUGS.has(s)) ?? []
  const laborStripNow = laborStrip && metricId === "labor"
  const bareStatNow = bareStats && ALIGNED_BARE_STAT_METRICS.has(metricId)
  const bareCapabilityNow = capabilityNoLevel && metricId === "capability"
  if (!detail) return null
  const firstYear = detail.xTicks[0]
  const lastYear = detail.xTicks[detail.xTicks.length - 1]
  return (
    <PanelFrame flush>
      <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
        <PanelBackButton onClose={onClose} design="chip-chevron" />
        <PanelTitle metricId={metricId} text={laborStripNow ? ALIGNED_LABOR_TITLE : undefined} className="min-w-0 flex-1" />
        <PanelHeaderSupplementLinks slugs={slugs} design={headerLinkDesign} />
      </div>

      <PowerStackSectionTitle sizeClass="text-[12px]" className={`mt-7 ${FADE_ANIM}`}>
        {POWER_STACK_SECTION_TITLES.temporal.now(Math.floor(year))}
      </PowerStackSectionTitle>
      {/* Tab 47: the definition rides directly under the NOW title as its
          subtitle; earlier tabs caption the number/visual with it instead.
          Capability (bareCapabilityNow) folds the definition into its
          combined row below either way. */}
      {definitionUnderTitle && !bareCapabilityNow && (
        <div className={`mt-1.5 font-et-book text-[13px] leading-[1.45] ${FADE_ANIM}`}>
          {METRIC_PANEL_DEFINITIONS[metricId].definition}
        </div>
      )}
      {laborStripNow ? (
        // The HUD strip re-rendered live at panel width — same
        // data-metric-panel-block flight slot / display-only treatment as
        // PanelClickedBlock. Legend layout splits on the app's mobile
        // breakpoint: phone columns can't fit both entries on one line
        // since the "at up to ×N speed" copy (the trailing speed was
        // getting clipped), so mobile stacks the rows with leader-aligned
        // speeds; desktop keeps the single inline line.
        <div data-metric-panel-block className="pointer-events-none mt-2">
          <div className="md:hidden">
            <LaborDotStripLeaderRows year={year} legendLayout="rows" />
          </div>
          <div className="hidden md:block">
            <LaborDotStripLeaderRows year={year} legendLayout="inline" />
          </div>
        </div>
      ) : bareStatNow ? (
        <AlignedStatNumber metricId={metricId} year={year} className="mt-2" />
      ) : bareCapabilityNow ? (
        // Capability: no big number — the definition left-aligned (flex-1,
        // wraps to leave room) with the speedup right-aligned in the hover
        // menu's stat treatment.
        <div className={`mt-1.5 flex items-start justify-between gap-4 ${FADE_ANIM}`}>
          <div className="min-w-0 flex-1 font-et-book text-[13px] leading-[1.45]">
            {METRIC_PANEL_DEFINITIONS[metricId].definition}
          </div>
          <AlignedCapabilitySpeedupLine year={year} />
        </div>
      ) : (
        <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-2" />
      )}
      {!definitionUnderTitle && !bareCapabilityNow && (
        <div className={`mt-1.5 font-et-book text-[13px] leading-[1.45] ${FADE_ANIM}`}>
          {METRIC_PANEL_DEFINITIONS[metricId].definition}
        </div>
      )}
      {metricId === "capability" && (
        <div className={`mt-2.5 ${IN_ANIM}`}>
          <PanelLiveExtras metricId={metricId} year={year} />
        </div>
      )}

      <div className={`mt-7 ${IN_ANIM}`}>
        <PowerStackSectionTitle sizeClass="text-[12px]" className="mb-1.5">
          {metricId === "safetyTax" ? SLOWDOWN_TOTAL_TITLE : POWER_STACK_TRAJECTORY_TITLE(firstYear, lastYear)}
        </PowerStackSectionTitle>
        {detail.explanation && (
          <div className="mb-2 font-et-book text-[13.5px] leading-[1.45] [&_b]:font-bold [&_em]:italic [&_li]:mb-1 [&_p]:mt-0 [&_p]:mb-[8px] [&_p:last-child]:mb-0 [&_ul]:mt-0 [&_ul]:mb-[8px] [&_ul]:list-disc [&_ul]:pl-4">
            {detail.explanation}
          </div>
        )}
        {/* The bar chart's own title caption is off here — the section title
            above already prints it. */}
        <PanelChart metricId={metricId} year={year} bottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} barChartWithCaption={false} />
      </div>

      <div className={`mt-4 flex gap-5 ${IN_ANIM}`}>
        <PowerStackScenarioBlock branch="D" heading={POWER_STACK_PLAN_D_SHORT_HEADING} body={detail.scenarios.D} bodyClassName="text-[13px]" headingClassName="text-[11px]" swatchWidth={POWER_STACK_COMPACT_SWATCH_WIDTH_PX} className="min-w-0 flex-1" />
        <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={detail.scenarios.A} bodyClassName="text-[13px]" headingClassName="text-[11px]" swatchWidth={POWER_STACK_COMPACT_SWATCH_WIDTH_PX} className="min-w-0 flex-1" />
        {otherPlans && <AlignedOtherPlansColumn metricId={metricId} />}
      </div>
    </PanelFrame>
  )
}

const PowerStackPanel = ({
  metricId,
  year,
  onClose,
  reference,
  // Tab 26: the breakdown sits DIRECTLY under the treemap — the hover menu
  // barely moves — with the chart below it, instead of chart-then-menu.
  menuFirst = false,
  // Tab 27: chart + treemap share the first row (see the comment above).
  chartRow = false,
  // Tab 32: the what-this-is explainer opens the panel (directly under the
  // header) instead of sitting below the chart.
  explainerTop = false,
  // Tab 37: the explainer rides as the chart section's SUBTITLE — directly
  // under the trajectory title, above the chart itself.
  explainerAtChart = false,
  // Tab 33: the reference prose matches the HOVER takeover's type system —
  // 9.5px sans bodies, 9.5px mono bold headers — so the panel reads as the
  // same instrument the hover was (title + year pill stay at their sizes).
  hoverType = false,
  // Tabs 34–35: mono zone titles gluing the two sections together — see
  // POWER_STACK_SECTION_TITLES for the vocabularies.
  sectionTitles = null,
  // Tab 39: a one-line body-size subtitle under each zone title — the NOW
  // zone's is the hover state's own subtitle (POWER_STACK_SECTION_SUBTITLES).
  sectionSubtitles = false,
  // Tab 40: the short "Measured in H100-equivalent chips" line as a figure
  // caption directly UNDER the treemap (POWER_STACK_TREEMAP_CAPTION).
  treemapCaption = false,
  // Tab 40: zone titles and the line-legend headings (Default path / Plan A /
  // Read more) at the HUD's standard 11px title size.
  standardTitleSize = false,
  // Replaces the panel title outright (unused since tab 42 reverted to the
  // metric label, but kept as a knob — pass e.g. POWER_STACK_DATA_TITLE).
  panelTitle,
  // Tab 42: no treemap at the top — the NOW zone is just the title, the
  // measured caption, and the breakdown columns. With the slot absent the
  // clicked block's flight is skipped (FlyingPanelElements only flies to a
  // present [data-metric-panel-block]).
  hideTreemap = false,
  // Tab 42: the Read-more supplement links as an inline row directly under
  // the header, before the NOW zone.
  readMoreTop = false,
  // Tab 42: grouped spacing rhythm — a LARGE gap (28px) before each zone
  // title, tight gaps inside a zone — so the NOW and TRAJECTORY groupings
  // read at a glance (the stock rhythm is a near-uniform 14–16px).
  groupedSpacing = false,
  // Tab 43: the supplement links stacked right-aligned IN the header row,
  // replacing the year readout (no Read-more heading). With the year slot
  // absent the pill's flight is skipped.
  readMoreInHeader = false,
  // Tab 43: the measured caption rides the NOW title's row, right-aligned,
  // instead of sitting under the treemap/title.
  captionOnTitleRow = false,
  // Tab 44: alternate first-column heading for the plans-3col reference.
  planDHeading = POWER_STACK_PLAN_D_HEADING,
  // Tab 44: shave px off the chart's bottom — with the axis floor hugging
  // the lowest line, the strip under the curves was pure whitespace; the
  // year labels ride up with it.
  chartBottomCropPx,
  // Tab 44: non-power metrics render the ALIGNED layout
  // (PowerStackAlignedMetricPanel) instead of falling back to power-duo.
  alignedFallback = false,
  // Tab 45: the aligned LABOR panel gets the strip treatment (title
  // ALIGNED_LABOR_TITLE, the HUD dot strip + inline legend as its NOW
  // visual) — see PowerStackAlignedMetricPanel's laborStrip.
  alignedLaborStrip = false,
  // Tab 46: the aligned panels' Other-plans third column, bare title-sized
  // NOW numbers for the stat metrics, and the capability stat without its
  // threshold tag — see PowerStackAlignedMetricPanel's matching knobs.
  alignedOtherPlans = false,
  alignedBareStats = false,
  alignedCapabilityNoLevel = false,
  // Tab 47: the aligned panels' definition as the NOW title's subtitle
  // (above the number/visual) — see PowerStackAlignedMetricPanel.
  alignedDefinitionUnderTitle = false,
  // Tabs 48–52: the header supplement links' chip treatment — see
  // PanelHeaderLinkDesign / PANEL_HEADER_LINK_STYLE. Applies to the power
  // header (readMoreInHeader) and the aligned panels alike.
  headerLinkDesign = "text",
}: PanelProps & {
  reference: PowerStackReference
  menuFirst?: boolean
  chartRow?: boolean
  explainerTop?: boolean
  explainerAtChart?: boolean
  hoverType?: boolean
  sectionTitles?: keyof typeof POWER_STACK_SECTION_TITLES | null
  sectionSubtitles?: boolean
  treemapCaption?: boolean
  standardTitleSize?: boolean
  panelTitle?: string
  hideTreemap?: boolean
  readMoreTop?: boolean
  groupedSpacing?: boolean
  readMoreInHeader?: boolean
  captionOnTitleRow?: boolean
  planDHeading?: string
  chartBottomCropPx?: number
  alignedFallback?: boolean
  alignedLaborStrip?: boolean
  alignedOtherPlans?: boolean
  alignedBareStats?: boolean
  alignedCapabilityNoLevel?: boolean
  alignedDefinitionUnderTitle?: boolean
  headerLinkDesign?: PanelHeaderLinkDesign
}) => {
  if (metricId !== "power") {
    return alignedFallback ? (
      <PowerStackAlignedMetricPanel metricId={metricId} year={year} onClose={onClose} laborStrip={alignedLaborStrip} otherPlans={alignedOtherPlans} bareStats={alignedBareStats} capabilityNoLevel={alignedCapabilityNoLevel} definitionUnderTitle={alignedDefinitionUnderTitle} headerLinkDesign={headerLinkDesign} />
    ) : (
      <PowerDuoPanel metricId={metricId} year={year} onClose={onClose} backDesign="chip-chevron" />
    )
  }
  // hoverType (production): reference prose in the essay's et-book serif at
  // reading sizes (2026-07-09 — the 9.5px system-sans register read as a
  // different product next to the article); the stock sizes keep the older
  // catalog tabs' system face.
  const explainerClass = hoverType ? "font-et-book text-[13.5px] leading-[1.45]" : "font-system-ui text-[13px] leading-normal"
  const refBodyClass = hoverType ? "text-[13px]" : "text-[12px]"
  const refHeadingClass = standardTitleSize ? "text-[11px]" : hoverType ? "text-[9.5px]" : "text-[10px]"
  const zoneTitleSize = standardTitleSize ? "text-[12px]" : "text-[10px]"
  const chart =
    reference === "chart-side" ? (
      <div className={`mt-3.5 flex items-start gap-5 ${IN_ANIM}`}>
        <div className="min-w-0 flex-[1.7]">
          <PanelChart metricId="power" year={year} />
        </div>
        <div className="w-[150px] shrink-0">
          <PowerStackScenarioBlock branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} bodyClassName="text-[11px]" />
          <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} bodyClassName="text-[11px]" className="mt-3" />
        </div>
      </div>
    ) : (
      <div className={`${groupedSpacing ? "mt-7" : "mt-3.5"} ${IN_ANIM}`}>
        {sectionTitles && (
          <PowerStackSectionTitle sizeClass={zoneTitleSize} className={sectionSubtitles ? "" : "mb-1.5"}>
            {POWER_STACK_SECTION_TITLES[sectionTitles].trajectory}
          </PowerStackSectionTitle>
        )}
        {sectionSubtitles && (
          <PowerStackSectionSubtitle className="mt-1 mb-2">
            {POWER_STACK_SECTION_SUBTITLES.trajectory}
          </PowerStackSectionSubtitle>
        )}
        {explainerAtChart && <div className={`mb-2 ${explainerClass}`}>{POWER_STACK_COPY.what}</div>}
        <PanelChart metricId="power" year={year} bottomCropPx={chartBottomCropPx} />
      </div>
    )
  // NO entrance animation on the breakdown: it is the below-HUD hover menu's
  // flight target (data-metric-panel-power-breakdown) — the traveller must
  // land on a completely still slot (see the power-duo note).
  const menu = (
    <div data-metric-panel-power-breakdown className={groupedSpacing ? "mt-2.5" : "mt-4"}>
      <PowerRegionsLiveBreakdown />
    </div>
  )
  return (
    <PanelFrame flush>
      <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
        <PanelBackButton onClose={onClose} design="chip-chevron" />
        <PanelTitle metricId={metricId} text={panelTitle} className="min-w-0 flex-1" />
        {readMoreInHeader ? (
          <PanelHeaderSupplementLinks
            slugs={METRIC_DETAILS.power?.sources.filter((s) => SUPPLEMENT_SLUGS.has(s)) ?? []}
            design={headerLinkDesign}
          />
        ) : (
          <PanelYearReadout year={year} />
        )}
      </div>
      {readMoreTop && (
        <div className={`mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 ${FADE_ANIM}`}>
          <b className={`font-mono font-bold uppercase tracking-wider ${refHeadingClass}`}>
            {POWER_STACK_READ_MORE_HEADING}
          </b>
          {(METRIC_DETAILS.power?.sources.filter((s) => SUPPLEMENT_SLUGS.has(s)) ?? []).map((slug) => (
            <a
              key={slug}
              href={`/supplements/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`font-system-ui leading-normal text-[#2A623D] underline decoration-[#2A623D]/30 underline-offset-2 hover:decoration-[#2A623D] ${refBodyClass}`}
            >
              {SUPPLEMENT_TITLES[slug] ?? slug}
            </a>
          ))}
        </div>
      )}
      {explainerTop && (
        <div className={`mt-3 ${explainerClass} ${FADE_ANIM}`}>{POWER_STACK_COPY.what}</div>
      )}
      {chartRow ? (
        <div className="mt-4 flex items-start gap-[20px]">
          <div className={`min-w-0 flex-1 ${IN_ANIM}`}>
            <PanelChart metricId="power" year={year} viewWidth={POWER_STACK_CHART_ROW_CHART_WIDTH_PX} />
          </div>
          <PanelClickedBlock metricId="power" year={year} size="hud" className="w-[220px] shrink-0" />
        </div>
      ) : (
        <>
          {sectionTitles && captionOnTitleRow && treemapCaption ? (
            <div className={`${groupedSpacing ? "mt-7" : "mt-4"} flex items-baseline justify-between gap-3 ${FADE_ANIM}`}>
              <PowerStackSectionTitle sizeClass={zoneTitleSize}>
                {sectionTitles === "temporal"
                  ? POWER_STACK_SECTION_TITLES.temporal.now(Math.floor(year))
                  : POWER_STACK_SECTION_TITLES.content.now}
              </PowerStackSectionTitle>
              <div className={hoverType ? "font-et-book text-[13px] leading-[1.45]" : "font-system-ui text-[12px] leading-normal"}>
                {POWER_STACK_TREEMAP_CAPTION}
              </div>
            </div>
          ) : sectionTitles ? (
            <PowerStackSectionTitle sizeClass={zoneTitleSize} className={`${groupedSpacing ? "mt-7" : "mt-4"} ${FADE_ANIM}`}>
              {sectionTitles === "temporal"
                ? POWER_STACK_SECTION_TITLES.temporal.now(Math.floor(year))
                : POWER_STACK_SECTION_TITLES.content.now}
            </PowerStackSectionTitle>
          ) : null}
          {sectionSubtitles && (
            <PowerStackSectionSubtitle className={`mt-1 ${FADE_ANIM}`}>
              {POWER_STACK_SECTION_SUBTITLES.now}
            </PowerStackSectionSubtitle>
          )}
          {!hideTreemap && (
            <PanelClickedBlock metricId="power" year={year} size="full" className={sectionSubtitles ? "mt-2" : sectionTitles ? "mt-1.5" : "mt-4"} />
          )}
          {treemapCaption && !captionOnTitleRow && (
            <div className={`mt-1.5 ${hoverType ? "font-et-book text-[13px] leading-[1.45]" : "font-system-ui text-[12px] leading-normal"} ${FADE_ANIM}`}>
              {POWER_STACK_TREEMAP_CAPTION}
            </div>
          )}
        </>
      )}
      {chartRow ? (
        menu
      ) : menuFirst ? (
        <>
          {menu}
          {chart}
        </>
      ) : (
        <>
          {chart}
          {menu}
        </>
      )}
      {reference === "sections" && (
        <div className={`mt-4 border-t border-black/50 pt-3 ${IN_ANIM}`}>
          <PowerStackScenarioBlock branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} />
          <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} className="mt-2.5" />
          <PanelSupplementLinks metricId="power" className="mt-2.5" />
        </div>
      )}
      {reference === "columns" && (
        <div className={`mt-4 border-t border-black/50 pt-3 ${IN_ANIM}`}>
          <div className="flex gap-5">
            <div className="min-w-0 flex-1">
              <b className="font-mono text-[10px] font-bold uppercase tracking-wider">{POWER_STACK_COPY.whatHeading}</b>
              <div className="mt-1 font-system-ui text-[12px] leading-normal">{POWER_STACK_COPY.what}</div>
            </div>
            <PowerStackScenarioBlock branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} bodyClassName="text-[12px]" className="min-w-0 flex-1" />
            <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} bodyClassName="text-[12px]" className="min-w-0 flex-1" />
          </div>
          <PanelSupplementLinks metricId="power" className="mt-2.5" />
        </div>
      )}
      {reference === "chart-side" && (
        <div className={`mt-3.5 ${IN_ANIM}`}>
          <PanelSupplementLinks metricId="power" />
        </div>
      )}
      {reference === "footnote" && (
        <div className={`mt-4 border-t border-gray-300 pt-2 ${IN_ANIM}`}>
          <div className="font-mono text-[10px] leading-snug">{POWER_STACK_COPY.what}</div>
          <PanelSupplementLinks metricId="power" className="mt-1.5" />
        </div>
      )}
      {/* No rule above the reference band (2026-07-07: it read as an x-axis
          line under the chart's year labels) — the spacing alone separates. */}
      {reference === "explainer-3col" && (
        <div className={`mt-4 pt-3 ${IN_ANIM}`}>
          {!explainerTop && !explainerAtChart && (
            <div className={explainerClass}>{POWER_STACK_COPY.what}</div>
          )}
          <div className={`flex gap-5 ${explainerTop || explainerAtChart ? "" : "mt-3"}`}>
            <PowerStackScenarioBlock branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} bodyClassName={refBodyClass} headingClassName={refHeadingClass} className="min-w-0 flex-1" />
            <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} bodyClassName={refBodyClass} headingClassName={refHeadingClass} className="min-w-0 flex-1" />
            <PowerStackReadMoreColumn className="min-w-0 flex-1" headingClassName={refHeadingClass} linkClassName={hoverType ? "text-[9.5px]" : "text-[12px]"} />
          </div>
        </div>
      )}
      {reference === "plans-3col" && (
        <div className={`${groupedSpacing ? "mt-4" : "mt-4 pt-3"} ${IN_ANIM}`}>
          {!explainerTop && !explainerAtChart && (
            <div className={explainerClass}>{POWER_STACK_COPY.what}</div>
          )}
          <div className={`flex gap-5 ${explainerTop || explainerAtChart ? "" : "mt-3"}`}>
            <PowerStackScenarioBlock branch="D" heading={planDHeading} body={POWER_STACK_COPY.D} bodyClassName={refBodyClass} headingClassName={refHeadingClass} swatchWidth={POWER_STACK_COMPACT_SWATCH_WIDTH_PX} className="min-w-0 flex-1" />
            <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} bodyClassName={refBodyClass} headingClassName={refHeadingClass} swatchWidth={POWER_STACK_COMPACT_SWATCH_WIDTH_PX} className="min-w-0 flex-1" />
            <div className="min-w-0 flex-1">
              {/* Gray like the B / C / S chart lines it annotates (baseline
                  since 2026-07-07; Plan D alone keeps the red). */}
              <b
                className={`font-mono font-bold uppercase tracking-wider ${refHeadingClass}`}
                style={{ color: OTHER_PLANS_GRAY }}
              >
                {POWER_STACK_OTHER_PLANS_HEADING}
              </b>
              <ul className="mt-1 grid list-none gap-1.5 p-0">
                {POWER_STACK_OTHER_PLANS.map((p) => (
                  <li key={p.id} className={`font-et-book leading-[1.45] ${refBodyClass}`}>
                    <b className="font-bold">{p.name}</b> — {p.trajectory}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {reference === "explainer-2col" && (
        <div className={`mt-4 border-t border-black/50 pt-3 ${IN_ANIM}`}>
          <div className="font-system-ui text-[13px] leading-normal">{POWER_STACK_COPY.what}</div>
          <div className="mt-3 flex gap-5">
            <PowerStackScenarioBlock branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} bodyClassName="text-[12px]" className="min-w-0 flex-1" />
            <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} bodyClassName="text-[12px]" className="min-w-0 flex-1" />
          </div>
          <PanelSupplementLinks metricId="power" className="mt-3" />
        </div>
      )}
      {reference === "footnotes-3col" && (
        <div className={IN_ANIM}>
          {/* Figure footnotes, columnized: tight under the chart (no rule —
              they read as part of the figure), one column per line style,
              the Read-more column closing the row. */}
          <div className="mt-2 flex gap-5">
            <PowerStackScenarioBlock branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} bodyClassName="text-[11px]" className="min-w-0 flex-1" />
            <PowerStackScenarioBlock branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} bodyClassName="text-[11px]" className="min-w-0 flex-1" />
            <PowerStackReadMoreColumn className="min-w-0 flex-1" />
          </div>
          <div className="mt-4 border-t border-gray-300 pt-2 font-mono text-[10px] leading-snug">
            {POWER_STACK_COPY.what}
          </div>
        </div>
      )}
      {reference === "chart-footnotes" && (
        <div className={IN_ANIM}>
          {/* Hugging the chart, figure-caption tight — these explain the two
              line styles the reader is looking at right above. */}
          <PowerStackChartFootnote branch="D" heading={POWER_STACK_COPY.DHeading} body={POWER_STACK_COPY.D} className="mt-2" />
          <PowerStackChartFootnote branch="A" heading={POWER_STACK_COPY.AHeading} body={POWER_STACK_COPY.A} className="mt-1.5" />
          <div className="mt-4 border-t border-gray-300 pt-2">
            <div className="font-mono text-[10px] leading-snug">{POWER_STACK_COPY.what}</div>
            <PanelSupplementLinks metricId="power" className="mt-1.5" />
          </div>
        </div>
      )}
    </PanelFrame>
  )
}

// Sketch 2 · "one-row": back arrow, year pill, and title share one header
// line; the clicked block spans the column beneath.
const OneRowPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <PanelYearReadout year={year} />
      <PanelTitle metricId={metricId} className="min-w-0 flex-1" />
    </div>
    <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-4" />
    <SketchLiveZone metricId={metricId} year={year} onClose={onClose} />
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// Sketch 3 · "year-first": the year pill sits alone at the very top — right
// about where the HUD's own pill lives, so it reads as staying put — with
// back + title on the line below.
const YearFirstPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={FADE_ANIM}>
      <PanelYearReadout year={year} />
    </div>
    <div className={`mt-2.5 flex items-center gap-3 ${FADE_ANIM}`}>
      <PanelBackButton onClose={onClose} />
      <PanelTitle metricId={metricId} />
    </div>
    <PanelClickedBlock metricId={metricId} year={year} size="wide" className="mt-3" />
    <SketchLiveZone metricId={metricId} year={year} onClose={onClose} />
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// Sketch 4 · "corner-dock": the year pill with an extra-large back button
// beneath it dock in the top-left corner while the clicked block sits to
// their right; the title runs below, then the live data and Details.
const CornerDockPanel = ({ metricId, year, onClose }: PanelProps) => (
  <PanelFrame>
    <div className={`flex items-start gap-4 ${FADE_ANIM}`}>
      <div className="flex shrink-0 flex-col items-start gap-2.5">
        <PanelYearReadout year={year} />
        <PanelBackButton onClose={onClose} className="!h-11 !w-14 text-[24px]" />
      </div>
      <PanelClickedBlock metricId={metricId} year={year} size="wide" className="min-w-0 flex-1" />
    </div>
    <div className={`mt-4 ${FADE_ANIM}`}>
      <PanelTitle metricId={metricId} />
    </div>
    <SketchLiveZone metricId={metricId} year={year} onClose={onClose} />
    <SketchDetailsSection metricId={metricId} />
  </PanelFrame>
)

// ─── "minimal" (production, 2026-07-09) ──────────────────────────────────────
// The deep-simplification pass over the power-stack lineage, paired with the
// terse copy rewrite (Thomas's voice). One structure for every metric:
//   header     — back chip, title, supplement-link chips
//   NOW        — zone title carrying the story year, the metric's one-line
//                definition, then the live visual (big number, dot strip,
//                treemap + region breakdown, or speedup line + ladder)
//   TRAJECTORY — zone title over the chart years, a one-line explainer, then
//                the chart with the plans below it as full-width hanging-
//                indent rows: swatch + mono plan name — one serif sentence.
//                The rows are the chart's legend and its commentary in one
//                place; no more three-column prose wall. Slowdown's bar chart
//                already compares the plans bar-by-bar, so it renders no rows.

// The TABLE's dotted plan swatch (2026-07-09): tuned to read like the labor
// legend's 1px dotted CSS leaders (1px round dots, ~2.5px pitch) — per
// Joanna, the table dots match the leaders; the CHART's dotted curves keep
// their own register (MetricScenarioChart's CONTEXT_DASH).
const TABLE_DOT_DASH = "0.1 2.4"
const SwOtherPlans = () => (
  <svg width="18" height="8" viewBox="0 0 18 8" className="inline-block align-middle" aria-hidden>
    <line x1="1" y1="4" x2="18" y2="4" stroke="#111111" strokeWidth="1" strokeDasharray={TABLE_DOT_DASH} strokeLinecap="round" />
  </svg>
)

// One plan as a hanging-indent row: the swatch gutters left, the name runs
// into the sentence so a wrapped line indents under the text, not the swatch.
const MinimalPlanRow = ({
  swatch,
  name,
  nameColor = "#111111",
  children,
}: {
  swatch: ReactNode
  name: string
  nameColor?: string
  children: ReactNode
}) => (
  <li className="grid grid-cols-[18px_1fr] items-baseline gap-x-2.5">
    <span className="mt-1 justify-self-start" aria-hidden>
      {swatch}
    </span>
    <span className="font-et-book text-[13px] leading-[1.45]">
      <b className="mr-1 font-mono text-[10px] font-bold uppercase tracking-wider" style={{ color: nameColor }}>
        {name}
      </b>
      {children}
    </span>
  </li>
)

// The supplement links as a closing "Read more" row — the same hairline chips
// the header used to carry, now left-aligned at the end of the panel where
// the reading flow wants them (2026-07-09: two stacked right-aligned chips
// broke the header's line). Tiny mono lead-in, then one chip per supplement.
const MinimalSupplementLinks = ({ metricId }: { metricId: MetricId }) => {
  const slugs = METRIC_DETAILS[metricId]?.sources.filter((s) => SUPPLEMENT_SLUGS.has(s)) ?? []
  if (slugs.length === 0) return null
  const S = PANEL_HEADER_LINK_STYLE
  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1.5">
      <b className="font-mono text-[10px] font-bold uppercase tracking-wider">
        {POWER_STACK_READ_MORE_HEADING}
      </b>
      {slugs.map((slug) => (
        <a
          key={slug}
          href={`/supplements/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center rounded-[3px] border border-[#111111] font-mono font-bold leading-none text-[#111111] no-underline transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
          style={{
            fontSize: S.labelFontSize,
            gap: S.iconGapPx,
            padding: `${S.paddingY}px ${S.paddingX}px`,
          }}
        >
          <HeaderLinkArrow />
          {SUPPLEMENT_TITLES[slug] ?? slug}
        </a>
      ))}
    </div>
  )
}

const MinimalMetricPanel = ({ metricId, year, onClose, staticYearPill }: PanelProps) => {
  const { detail } = useMetricPanelDetail(metricId)
  if (!detail) return null
  const isBarChart = metricId === "safetyTax"
  const bareStat = ALIGNED_BARE_STAT_METRICS.has(metricId)
  // Power's Other-plans copy lives with the power-stack panels; the rest come
  // from the shared per-metric dict.
  const otherPlans = metricId === "power" ? POWER_STACK_OTHER_PLANS : METRIC_OTHER_PLANS[metricId]
  return (
    <PanelFrame flush>
      {/* One clean line: outlined back square, title, and the live year at
          the right — no "NOW" label. The year slot is an INVISIBLE
          placeholder: the actual pill is the overlay DetailsYearPill, which
          hides the graph's pill, glides from its spot to this slot on open,
          and stays glued to it (one real element — never a duplicate). The
          stat metrics read as one phrase — "1.7K in 2029" — with the pill
          landing on the phrase's year; the pill's own 11px text inset
          provides the word-space after "in". */}
      <div className={`flex items-center gap-3 ${FADE_ANIM}`}>
        <PanelBackButton onClose={onClose} design="outline-arrow" />
        <PanelTitle metricId={metricId} text={metricId === "labor" ? ALIGNED_LABOR_TITLE : undefined} className="min-w-0 flex-1" />
        {bareStat || metricId === "capability" ? (
          <span className="flex shrink-0 items-baseline">
            <AlignedStatNumber metricId={metricId} year={year} sizeClass="text-[17px]" />
            {/* ml matches the pill's 11px text inset on the other side of
                "in", so the phrase's two word-spaces are symmetric. */}
            <span className="ml-[11px] font-mono text-[17px] leading-none">in</span>
            <PanelYearReadout year={year} className={staticYearPill ? "" : "invisible"} />
          </span>
        ) : (
          <PanelYearReadout year={year} className={staticYearPill ? "" : "invisible"} />
        )}
      </div>

      {metricId === "power" ? (
        <>
          <PanelClickedBlock metricId="power" year={year} size="full" className="mt-2" />
          <div className={`mt-1.5 font-et-book text-[13px] leading-[1.45] ${FADE_ANIM}`}>
            {POWER_STACK_TREEMAP_CAPTION} Compute going to frontier AI development is outlined in{" "}
            <span className="font-semibold text-[#8b0000]">red</span>.
          </div>
          {/* Flight target for the below-HUD hover menu
              (data-metric-panel-power-breakdown) — must stay still, so no
              entrance animation here. */}
          <div data-metric-panel-power-breakdown className="mt-2.5">
            <PowerRegionsLiveBreakdown />
          </div>
        </>
      ) : metricId === "labor" ? (
        // The HUD strip re-rendered live at panel width — same flight slot /
        // display-only treatment as PanelClickedBlock. Legend layout splits
        // on the app's mobile breakpoint: phone columns can't fit both
        // entries on one line since the "at up to ×N speed" copy, so mobile
        // stacks the rows with leader-aligned speeds.
        <div data-metric-panel-block className="pointer-events-none mt-2">
          <div className="md:hidden">
            <LaborDotStripLeaderRows year={year} legendLayout="rows" />
          </div>
          <div className="hidden md:block">
            <LaborDotStripLeaderRows year={year} legendLayout="inline" />
          </div>
        </div>
      ) : metricId === "capability" ? (
        // The speedup value lives in the header phrase ("2× in 2029") —
        // same treatment as the stat metrics — so here it's just the
        // definition, then the ladder.
        <>
          <div className={`mt-2.5 font-et-book text-[13px] leading-[1.45] ${FADE_ANIM}`}>
            {METRIC_PANEL_DEFINITIONS.capability.definition}
          </div>
          <div className={`mt-2.5 ${IN_ANIM}`}>
            <PanelLiveExtras metricId={metricId} year={year} />
          </div>
        </>
      ) : (
        // The four stat metrics: the value lives in the header phrase
        // ("1.7K in 2029"), so below it is just the merged definition +
        // explainer paragraph.
        <div className={`mt-2.5 font-et-book text-[13px] leading-[1.45] ${FADE_ANIM}`}>
          {METRIC_PANEL_DEFINITIONS[metricId].definition} {detail.explanation}
        </div>
      )}

      <div className={`mt-7 ${IN_ANIM}`}>
        {/* No "TRAJECTORY — 2024 → 2040" label: the chart's axis years and
            now-dot carry that. The slowdown bar chart and power's share
            chart keep real titles — those are information, not scaffolding
            (power's names the plotted quantity, which is NOT the treemap
            above it). */}
        {(isBarChart || metricId === "power") && (
          <PowerStackSectionTitle sizeClass="text-[12px]" className="mb-1.5">
            {isBarChart ? SLOWDOWN_TOTAL_TITLE : POWER_STACK_SECTION_TITLES.temporal.trajectory}
          </PowerStackSectionTitle>
        )}
        {/* The stat metrics carry their explainer in the top paragraph. */}
        {detail.explanation && !bareStat && (
          <div className="mb-2 font-et-book text-[13.5px] leading-[1.45]">{detail.explanation}</div>
        )}
        <PanelChart metricId={metricId} year={year} bottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} barChartWithCaption={false} />
        {!isBarChart && (
          <ul className="mt-4 grid list-none gap-2 p-0">
            <MinimalPlanRow swatch={<SwBranchStack branch="D" />} name={POWER_STACK_PLAN_D_SHORT_HEADING} nameColor="var(--race-background)">
              {detail.scenarios.D}
            </MinimalPlanRow>
            <MinimalPlanRow swatch={<SwBranchStack branch="A" />} name={POWER_STACK_COPY.AHeading}>
              {detail.scenarios.A}
            </MinimalPlanRow>
            {otherPlans?.map((p) => (
              <MinimalPlanRow key={p.id} swatch={<SwOtherPlans />} name={p.name}>
                {p.trajectory}
              </MinimalPlanRow>
            ))}
          </ul>
        )}
        <MinimalSupplementLinks metricId={metricId} />
      </div>
    </PanelFrame>
  )
}

const PANEL_DESIGN_RENDERERS: Record<MetricPanelDesignId, (props: PanelProps) => ReactNode> = {
  "title-row": TitleRowPanel,
  "title-row-year-right": TitleRowYearRightPanel,
  "title-row-details-flush": TitleRowDetailsFlushPanel,
  "power-duo": PowerDuoPanel,
  "power-duo-back-red": (props) => <PowerDuoPanel {...props} backDesign="red" />,
  "power-duo-back-chip": (props) => <PowerDuoPanel {...props} backDesign="chip" />,
  "power-duo-back-labeled": (props) => <PowerDuoPanel {...props} backDesign="labeled" />,
  "power-duo-back-long": (props) => <PowerDuoPanel {...props} backDesign="long" />,
  "power-duo-back-solid": (props) => <PowerDuoPanel {...props} backDesign="solid" />,
  "power-duo-back-chip-arrow": (props) => <PowerDuoPanel {...props} backDesign="chip-arrow" />,
  "power-duo-back-chip-chevron": (props) => <PowerDuoPanel {...props} backDesign="chip-chevron" />,
  "power-duo-back-chip-chevrons": (props) => <PowerDuoPanel {...props} backDesign="chip-chevrons" />,
  "power-duo-back-chip-return": (props) => <PowerDuoPanel {...props} backDesign="chip-return" />,
  "power-duo-back-chip-triangle": (props) => <PowerDuoPanel {...props} backDesign="chip-triangle" />,
  "power-stack-2sent": (props) => <PowerStackPanel {...props} reference="sections" />,
  "power-stack-3col": (props) => <PowerStackPanel {...props} reference="columns" />,
  "power-stack-chart-side": (props) => <PowerStackPanel {...props} reference="chart-side" />,
  "power-stack-footnote": (props) => <PowerStackPanel {...props} reference="footnote" />,
  "power-stack-menu-first": (props) => <PowerStackPanel {...props} reference="sections" menuFirst />,
  "power-stack-chart-row": (props) => <PowerStackPanel {...props} reference="columns" chartRow />,
  "power-stack-explainer-3col": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst />,
  "power-stack-explainer-2col": (props) => <PowerStackPanel {...props} reference="explainer-2col" menuFirst />,
  "power-stack-chart-footnotes": (props) => <PowerStackPanel {...props} reference="chart-footnotes" menuFirst />,
  "power-stack-footnotes-3col": (props) => <PowerStackPanel {...props} reference="footnotes-3col" menuFirst />,
  "power-stack-intro-top": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst explainerTop />,
  "power-stack-hover-type": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst hoverType />,
  "power-stack-section-titles": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst sectionTitles="content" />,
  "power-stack-temporal-titles": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst sectionTitles="temporal" />,
  "power-stack-framed": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst explainerTop hoverType sectionTitles="content" />,
  "power-stack-framed-chart-sub": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst explainerAtChart hoverType sectionTitles="content" />,
  "power-stack-hover-type-titles": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst hoverType sectionTitles="content" />,
  "power-stack-temporal-subtitled": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst sectionTitles="temporal" sectionSubtitles />,
  "power-stack-temporal-caption": (props) => <PowerStackPanel {...props} reference="explainer-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize />,
  "power-stack-plans-3col": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize />,
  "power-stack-data-behind": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize hideTreemap readMoreTop groupedSpacing />,
  "power-stack-header-links": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption captionOnTitleRow hoverType standardTitleSize hideTreemap readMoreInHeader groupedSpacing />,
  "power-stack-plans-grouped": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback />,
  "power-stack-labor-strip": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip />,
  "power-stack-aligned-plans": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel />,
  "power-stack-gray-plans": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle />,
  "power-stack-links-outline": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle headerLinkDesign="outline" />,
  "power-stack-links-solid": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle headerLinkDesign="solid" />,
  "power-stack-links-red-arrow": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle headerLinkDesign="red-arrow" />,
  "power-stack-links-bare": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle headerLinkDesign="bare" />,
  "power-stack-links-pill": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle headerLinkDesign="pill" />,
  "power-stack-links-outline-clean": (props) => <PowerStackPanel {...props} reference="plans-3col" menuFirst sectionTitles="temporal" explainerAtChart treemapCaption hoverType standardTitleSize readMoreInHeader groupedSpacing planDHeading={POWER_STACK_PLAN_D_SHORT_HEADING} chartBottomCropPx={POWER_STACK_CHART_BOTTOM_CROP_PX} alignedFallback alignedLaborStrip alignedOtherPlans alignedBareStats alignedCapabilityNoLevel alignedDefinitionUnderTitle headerLinkDesign="outline-clean" />,
  minimal: MinimalMetricPanel,
  "one-row": OneRowPanel,
  "year-first": YearFirstPanel,
  "corner-dock": CornerDockPanel,
  strip: StripPanel,
  dossier: DossierPanel,
  rail: RailPanel,
  ticker: TickerPanel,
  ledger: LedgerPanel,
}

export const MetricPanelRedesign = ({
  designId,
  ...props
}: PanelProps & { designId: MetricPanelDesignId }) => {
  const Design = PANEL_DESIGN_RENDERERS[designId]
  return <Design {...props} />
}
